/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { useAppSelector } from "src/store";
import { useGetCampaignConfigsApi, useDeleteCampaignConfigApi, useCreateCampaignConfigApi, useUpdateCampaignConfigApi } from '../api';
import { setLoading, setError, setCampaignConfigs, updateFilters, deleteCampaignConfig as deleteCampaignConfigAction } from '../slices';
import { AdminCampaignsState } from '../slices/types';
import { GetCampaignConfigsParams } from '../types';
import { getCurrentCompanyId } from '../../companies/slices';
import { Company } from "src/constants/types";
import { useCampaignConfigLoader } from "src/modules/campaignConfigBuilder/hooks";

export const useAdminCampaignsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<{ adminCampaigns: AdminCampaignsState; companies: Company[] }>();
  const { isLoading, filters, campaignConfigs, totalCount, error } = useAppSelector((state) => state.adminCampaigns);
  const currentCompanyId = useAppSelector(getCurrentCompanyId);

  const getCampaignConfigsApi = useGetCampaignConfigsApi();
  const deleteCampaignConfigApi = useDeleteCampaignConfigApi();
  const createCampaignConfigApi = useCreateCampaignConfigApi();
  const updateCampaignConfigApi = useUpdateCampaignConfigApi();
  const { loadCampaignConfigs } = useCampaignConfigLoader();

  const getCampaignConfigs = useCallback(async () => {
    const currentFilters = getState().adminCampaigns.filters;
    // When filtering by application and the user hasn't picked an explicit sort,
    // default the BE sort to applicationName so app campaigns group together.
    const params = {
      ...currentFilters,
      sort: currentFilters.sort ?? (currentFilters.applicationIds ? 'application_name_asc' : undefined),
    };

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const response = await getCampaignConfigsApi(params, currentCompanyId);
      dispatch(setCampaignConfigs({ items: response.data.items, count: response.data.count }));
      return response.data;
    } catch (error) {
      dispatch(setError('Failed to fetch campaign configurations'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getCampaignConfigsApi, getState, currentCompanyId]);

  const deleteCampaignConfig = useCallback(async (id: string) => {
    try {
      await deleteCampaignConfigApi(id);
      // Remove the archived config from the state
      dispatch(deleteCampaignConfigAction(id));
      await loadCampaignConfigs(true);
    } catch (error) {
      dispatch(setError('Failed to delete campaign configuration'));
      throw error;
    }
  }, [dispatch, deleteCampaignConfigApi, loadCampaignConfigs]);

  const createCampaignConfig = useCallback(async (data: { applicationIds: string[] | null; config: string }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const apiData = {
        applicationIds: data.applicationIds,
        config: JSON.parse(data.config)
      };
      const response = await createCampaignConfigApi(apiData);
      // Refresh the campaign configs list after creating
      await getCampaignConfigs();
      return response.data;
    } catch (error) {
      dispatch(setError('Failed to create campaign configuration'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, createCampaignConfigApi, getCampaignConfigs]);

  const updateCampaignConfig = useCallback(async (id: string, data: { applicationIds: string[] | null; config: string; status: 'active' | 'archived' }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const apiData = {
        applicationIds: data.applicationIds,
        config: JSON.parse(data.config),
        status: data.status
      };
      const response = await updateCampaignConfigApi(id, apiData);
      // Refresh the campaign configs list after updating
      await getCampaignConfigs();
      await loadCampaignConfigs(true);
      return response.data;
    } catch (error) {
      dispatch(setError('Failed to update campaign configuration'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, updateCampaignConfigApi, getCampaignConfigs, loadCampaignConfigs]);

  const changeFilters = useCallback((newFilters: Partial<GetCampaignConfigsParams>) => {
    dispatch(updateFilters(newFilters));
  }, [dispatch]);

  return {
    isLoading,
    filters,
    campaignConfigs,
    totalCount,
    error,
    getCampaignConfigs,
    deleteCampaignConfig,
    createCampaignConfig,
    updateCampaignConfig,
    changeFilters,
  };
};
